import {
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { SortableContext, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { type DigestSection, DragItemType, SECTION_COLORS } from "@utils/types";
import { useMemo, useState } from "react";

import { Button, Dialog, Heading, Paragraph, SlideOver } from "@fronterahealth/frontera-ui-components";

import { type GetCommunityPostsOutput } from "@api/rest/types-and-hooks";
import {
  type DigestPost,
  type PublishCommunityDigestInput,
  useBasecampCommunityPreviewDigestCreate,
  useBasecampCommunityPublishDigestCreate,
} from "@api/rest/types-and-hooks";
import { Breadcrumb } from "@components/Breadcrumb/Breadcrumb";
import { SortableItem } from "@components/SortableItem/SortableItem";
import { notifyError, notifySuccess } from "@components/notifications/notifications";

import { PopularSection } from "./components/PopularSection";
import { PostRow } from "./components/PostRow";
import { SectionCard } from "./components/SectionCard";

const makeDefaultSections = (): DigestSection[] => [
  { id: crypto.randomUUID(), header: "More highlights", color: SECTION_COLORS[0], posts: [], collapsed: false },
  {
    id: crypto.randomUUID(),
    header: "Parents found these helpful",
    color: SECTION_COLORS[1],
    posts: [],
    collapsed: false,
  },
  {
    id: crypto.randomUUID(),
    header: "Questions parents are asking",
    color: SECTION_COLORS[2],
    posts: [],
    collapsed: false,
  },
];

export const CommunityDigest: React.FC = () => {
  const [emailSubject, setEmailSubject] = useState("What parents are talking about this week");
  const [popularPost, setPopularPost] = useState<GetCommunityPostsOutput | null>(null);
  const [popularDescription, setPopularDescription] = useState("");
  const [sections, setSections] = useState<DigestSection[]>(makeDefaultSections());
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [activeDragPost, setActiveDragPost] = useState<{ post: GetCommunityPostsOutput; sectionColor: string } | null>(
    null,
  );
  const { mutateAsync: publishDigest, isPending: isPublishing } = useBasecampCommunityPublishDigestCreate();
  const { mutateAsync: previewDigest, isPending: isPreviewing } = useBasecampCommunityPreviewDigestCreate();

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const allSelectedPostIds = useMemo(
    () => [...(popularPost ? [popularPost.id] : []), ...sections.flatMap((s) => s.posts.map((p) => p.id))],
    [popularPost, sections],
  );

  const updateSection = (id: string, patch: Partial<DigestSection>) =>
    setSections((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));

  const addSection = () =>
    setSections((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        header: "New section",
        color: SECTION_COLORS[prev.length % SECTION_COLORS.length],
        posts: [],
        collapsed: false,
      },
    ]);

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    if (active.data.current?.type === DragItemType.POST) {
      const sectionId = active.data.current.sectionId as string;
      const section = sections.find((s) => s.id === sectionId);
      const post = section?.posts.find((p) => p.id.toString() === active.id);
      if (post && section) {
        const idx = sections.indexOf(section);
        const color = section.color ?? SECTION_COLORS[idx % SECTION_COLORS.length];
        setActiveDragPost({ post, sectionColor: color });
      }
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveDragPost(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeType = active.data.current?.type as string | undefined;
    const overType = over.data.current?.type as string | undefined;

    // --- Section reorder ---
    if (activeType === DragItemType.SECTION) {
      const oldIndex = sections.findIndex((s) => s.id === active.id);
      const newIndex = sections.findIndex((s) => s.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;
      setSections((prev) => arrayMove(prev, oldIndex, newIndex));
      return;
    }

    // --- Post drag (within section or cross-section) ---
    if (activeType === DragItemType.POST) {
      const sourceSectionId = active.data.current?.sectionId as string;
      const targetSectionId =
        overType === DragItemType.POST
          ? (over.data.current?.sectionId as string)
          : overType === DragItemType.SECTION
            ? (over.id as string)
            : null;

      if (!sourceSectionId || !targetSectionId) return;

      if (sourceSectionId === targetSectionId) {
        // Same section — reorder
        setSections((prev) =>
          prev.map((s) => {
            if (s.id !== sourceSectionId) return s;
            const oldIdx = s.posts.findIndex((p) => p.id.toString() === active.id);
            const newIdx = s.posts.findIndex((p) => p.id.toString() === over.id);
            if (oldIdx === -1 || newIdx === -1) return s;
            return { ...s, posts: arrayMove(s.posts, oldIdx, newIdx) };
          }),
        );
      } else {
        // Cross-section — move post from source to target
        setSections((prev) => {
          const activePost = prev
            .find((s) => s.id === sourceSectionId)
            ?.posts.find((p) => p.id.toString() === active.id);
          if (!activePost) return prev;

          return prev.map((s) => {
            if (s.id === sourceSectionId) {
              return { ...s, posts: s.posts.filter((p) => p.id.toString() !== active.id) };
            }
            if (s.id === targetSectionId) {
              if (overType === DragItemType.POST) {
                const overIdx = s.posts.findIndex((p) => p.id.toString() === over.id);
                const newPosts = [...s.posts];
                newPosts.splice(overIdx >= 0 ? overIdx : newPosts.length, 0, activePost);
                return { ...s, posts: newPosts };
              }
              return { ...s, posts: [...s.posts, activePost] };
            }
            return s;
          });
        });
      }
    }
  };

  const toPostPayload = (p: GetCommunityPostsOutput): DigestPost => ({
    postId: String(p.id),
    spaceId: String(p.space_id),
    postTitle: p.name,
    postAuthor: p.user_name,
    spaceName: p.space_name,
    totalComments: p.comment_count,
    totalLikes: p.like_count,
  });

  const buildPayload = (popular: GetCommunityPostsOutput): PublishCommunityDigestInput => ({
    email_subject: emailSubject,
    popular: {
      postId: String(popular.id),
      spaceId: String(popular.space_id),
      description: popularDescription,
      postTitle: popular.name,
      postAuthor: popular.user_name,
      spaceName: popular.space_name,
      totalComments: popular.comment_count,
      totalLikes: popular.like_count,
    },
    sections: sections.map((s) => ({
      sectionTitle: s.header,
      sectionColor: s.color,
      posts: s.posts.map(toPostPayload),
    })),
  });

  const handlePreview = async () => {
    if (!popularPost) {
      notifyError("Select a featured post before previewing.");
      return;
    }
    try {
      const response = await previewDigest({ data: buildPayload(popularPost) });
      if (response.status === 200) {
        setPreviewHtml(response.data.html);
      } else {
        notifyError(response.data?.detail ?? "Failed to render preview. Please try again.");
      }
    } catch {
      notifyError("Failed to render preview. Please try again.");
    }
  };

  const handlePublish = async () => {
    if (!emailSubject.trim()) {
      notifyError("Email subject is required.");
      setPublishModalOpen(false);
      return;
    }
    if (!popularPost) {
      notifyError("Select a featured post before publishing.");
      setPublishModalOpen(false);
      return;
    }
    if (!popularDescription.trim()) {
      notifyError("Add a description for the featured post before publishing.");
      setPublishModalOpen(false);
      return;
    }

    try {
      const response = await publishDigest({ data: buildPayload(popularPost) });
      if (response.status === 200) {
        notifySuccess("Digest published! Notifications are sending.");
        setPublishModalOpen(false);
        // Navigate to the same route is a no-op in React Router, so reset state manually.
        setEmailSubject("What parents are talking about this week");
        setPopularPost(null);
        setPopularDescription("");
        setSections(makeDefaultSections());
      } else {
        notifyError(response.data?.detail ?? "Failed to publish digest. Please try again.");
      }
    } catch {
      notifyError("Failed to publish digest. Please try again.");
      setPublishModalOpen(false);
    }
  };

  return (
    <div className="flex flex-col min-h-full">
      <div className="flex items-end justify-between px-16 pt-8 pb-4 shrink-0">
        <div className="flex flex-col gap-y-2">
          <Breadcrumb
            crumbs={[{ label: "Notifications", to: "/notifications/community-digest" }, { label: "Community Digest" }]}
          />
          <Heading size="medium">Community Digest</Heading>
        </div>
        <div className="flex items-center gap-x-2">
          <Button
            variant="primary"
            buttonStyle="border"
            size="sm"
            text={isPreviewing ? "Loading…" : "Preview"}
            disabled={isPreviewing}
            onClick={() => void handlePreview()}
          />
          <Button
            variant="primary"
            buttonStyle="filled"
            size="sm"
            text="Publish"
            onClick={() => setPublishModalOpen(true)}
          />
        </div>
      </div>

      <div className="flex flex-col gap-y-4 px-16 pb-12">
        <div className="rounded-xl border border-border-medium bg-bg-surface p-4 flex flex-col gap-y-1">
          <label htmlFor="email-subject" className="text-xs font-medium text-text-secondary">
            Email subject
          </label>
          <input
            id="email-subject"
            className="bg-transparent text-sm font-medium text-text-primary outline-none border-none ring-0 focus:ring-0 p-0 placeholder:text-text-tertiary"
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.target.value)}
            placeholder="What parents are talking about this week"
          />
        </div>

        <PopularSection
          post={popularPost}
          description={popularDescription}
          onSelectPost={setPopularPost}
          onRemovePost={() => {
            setPopularPost(null);
            setPopularDescription("");
          }}
          onDescriptionChange={setPopularDescription}
        />

        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={sections.map((s) => s.id)} strategy={verticalListSortingStrategy}>
            <div className="flex flex-col gap-y-4">
              {sections.map((section, index) => (
                <SortableItem key={section.id} id={section.id} data={{ type: DragItemType.SECTION }}>
                  {(sectionDragHandleProps) => (
                    <SectionCard
                      section={section}
                      index={index}
                      allSelectedPostIds={allSelectedPostIds}
                      dragHandleProps={sectionDragHandleProps}
                      onToggleCollapse={() => updateSection(section.id, { collapsed: !section.collapsed })}
                      onRenameHeader={(header) => updateSection(section.id, { header })}
                      onAddPost={(post) => updateSection(section.id, { posts: [...section.posts, post] })}
                      onRemovePost={(postId: number) =>
                        updateSection(section.id, { posts: section.posts.filter((p) => p.id !== postId) })
                      }
                      onDelete={() => setSections((prev) => prev.filter((s) => s.id !== section.id))}
                    />
                  )}
                </SortableItem>
              ))}
            </div>
          </SortableContext>

          <DragOverlay dropAnimation={null}>
            {activeDragPost && (
              <PostRow
                post={activeDragPost.post}
                accentColor={activeDragPost.sectionColor}
                showDragHandle={false}
                onRemove={() => {}}
              />
            )}
          </DragOverlay>
        </DndContext>

        <div className="group flex items-center gap-x-4 py-2 cursor-pointer" onClick={addSection}>
          <div className="flex-1 h-px bg-limestone-300 opacity-0 group-hover:opacity-100 transition-opacity" />
          <Button
            variant="primary"
            buttonStyle="filled"
            size="sm"
            text="+ Add Section"
            className="opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
          />
          <div className="flex-1 h-px bg-limestone-300 opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </div>

      <SlideOver
        open={previewHtml !== null}
        setOpen={(open) => !open && setPreviewHtml(null)}
        title="Digest preview"
        size="xl"
        contentClassName="p-0"
      >
        <iframe
          title="Community digest preview"
          srcDoc={previewHtml ?? ""}
          className="w-full h-full border-0 bg-bg-default"
        />
      </SlideOver>

      <Dialog
        open={publishModalOpen}
        setOpen={setPublishModalOpen}
        title="Publish this digest?"
        size="sm"
        showCloseButton={false}
        primaryButton={{
          text: isPublishing ? "Publishing..." : "Publish",
          disabled: isPublishing,
          onClick: () => void handlePublish(),
        }}
        secondaryButton={{
          text: "Cancel",
          onClick: () => setPublishModalOpen(false),
        }}
      >
        <Paragraph size="small" colorType="secondary" className="py-4">
          This will send a notification to all community members and cannot be undone.
        </Paragraph>
      </Dialog>
    </div>
  );
};
